// Имена событий аналитики — чистая логика без window (тесты). Экран — из маршрута, действие — из адреса пишущего
// запроса: адреса даёт сервер, поэтому списка действий у клиента нет — новое действие бэкенда появится в отчётах само.
// Словарь — MLS-Backend docs/analytics/events.yaml (v1). Ограничения GA4: имя события — до 40 знаков, латиница/цифры/
// подчёркивание, с буквы.

const clean = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/** Вкладка нижней панели (серверный ключ) → имя корневого экрана по словарю v1 (§4.1). Незнакомый ключ — сам ключ. */
const TAB_SCREENS: Record<string, string> = {
  matches: 'match_center', leagues: 'leagues', favorites: 'favorites', news: 'news_hub', highlights: 'highlights',
};
export const tabScreen = (key: string) => TAB_SCREENS[key] ?? (clean(key).slice(0, 40) || 'match_center');

/** Экраны сущностей по словарю v1 (§4.1): первый сегмент адреса (или вид экрана «Скоро») → имя. */
const ENTITY_SCREENS: Record<string, string> = {
  match: 'match_detail', league: 'league_detail', team: 'team_detail', player: 'player_detail',
  news: 'article_detail', highlight: 'highlight_player', search: 'search', 'world-cup': 'league_detail',
};

/** Маршрут → имя экрана словаря v1: «/match/123» → match_detail, «/team/33» → team_detail, «/world-cup» → league_detail,
 *  «/tab/matches» → match_center, «/menu» → settings, «/menu/notifications» → notification_settings. Старый адрес «Скоро» — имя экрана, который на этом месте:
 *  «/soon/team/33» → team_detail, «/soon/tab/leagues» → leagues. Номера в имя не идут — номер едет параметром content_id. */
export function screenName(pathname: string): string {
  const parts = pathname.split('?')[0]!.split('/').filter(Boolean);
  if (parts.length === 0) return 'match_center';
  if (parts[0] === 'tab') return tabScreen(parts[1] ?? '');
  if (parts[0] === 'menu') return parts[1] === 'notifications' ? 'notification_settings' : 'settings';
  if (parts[0] === 'soon') {
    if (parts[1] === 'tab') return tabScreen(parts[2] ?? '');
    return ENTITY_SCREENS[parts[1] ?? ''] ?? (clean(parts[1] ?? '') || 'match_center');
  }
  if (parts.length <= 2 && ENTITY_SCREENS[parts[0]!]) return ENTITY_SCREENS[parts[0]!]!;
  return clean(parts.filter((p) => !/\d/.test(p)).join('_')).slice(0, 40) || 'match_center';
}

/** Номер сущности из адреса экрана («/match/71314» → "71314", «/soon/team/33» → "33") — параметр content_id. */
export const screenContentId = (pathname: string) => /\/(\d{1,12})(?:[/?]|$)/.exec(pathname)?.[1];

/** Состояние матча по словарю v1: идущий — только по is_live сервера; NS/TBD — не начался; FT/AET/PEN — сыгран; прочее
 *  (перенесён, отменён, прерван) — other. Статус — короткий провайдерский: status_short или status.short строки матча. */
export type MatchState = 'live' | 'upcoming' | 'finished' | 'other';
const FINISHED = new Set(['FT', 'AET', 'PEN']);
const UPCOMING = new Set(['NS', 'TBD']);
export interface MatchLike {
  id: number; is_live?: boolean | null; status_short?: string | null;
  status?: { short?: string | null } | string | null; league?: { id: number } | null;
}
const shortStatus = (m: Omit<MatchLike, 'id'>) => m.status_short ?? (typeof m.status === 'string' ? m.status : m.status?.short) ?? '';
export function matchState(m: Omit<MatchLike, 'id'>): MatchState {
  if (m.is_live === true) return 'live';
  const s = shortStatus(m);
  if (UPCOMING.has(s)) return 'upcoming';
  if (FINISHED.has(s)) return 'finished';
  return 'other';
}

/** Параметры матча в content_open: состояние и лига (номер строкой; нет лиги — без параметра). */
export const matchContent = (m: MatchLike) => ({ match_state: matchState(m), league_id: m.league?.id != null ? String(m.league.id) : undefined });

/** match_detail_tab_select: только смена вкладки человеком — нажатие на открытую вкладку не событие (null). */
export function matchTabSelect(key: string, active: string, m: MatchLike) {
  return key === active ? null : { tab: key, fixture_id: String(m.id), match_state: matchState(m) };
}

/** Фильтр матч-центра (словарь v1 §4.3): all, live, planned, finished. */
export type MatchCenterFilter = 'all' | 'live' | 'planned' | 'finished';

/** Что не считаем действием: вход и обновление сессии (служебное), опрос живых матчей. */
const SKIP = /\/account\/(guest|telegram|refresh)\/|\/live\/batch\//;

/** Адрес пишущего запроса → имя события: «…/account/favorites/team/33/» (DELETE) → a_account_favorites_team_delete.
 *  null — не действие человека. Префикс a_ отделяет действия от штатных событий Google. */
export function actionName(method: string, path: string): string | null {
  if (method === 'GET' || SKIP.test(path)) return null;
  const tail = path.split('?')[0]!.replace(/^https?:\/\/[^/]+/, '').replace(/^\/?api\/v\d+\/mobile\//, '');
  const words = tail.split('/').filter((p) => p && !/^\d+$/.test(p) && !/^[0-9a-f-]{16,}$/i.test(p)).map(clean).filter(Boolean);
  if (words.length === 0) return null;
  // DELETE/PUT/PATCH на тот же адрес — другое действие (снять избранное)
  const verb = method === 'POST' ? '' : `_${method.toLowerCase()}`;
  return `a_${words.join('_')}`.slice(0, 40 - verb.length).replace(/_+$/, '') + verb;
}

/** Исход действия одним словом: ok, либо машинная причина отказа, либо код/статус. */
export function actionResult(status: number, code: string | null, reason: unknown): string {
  if (status >= 200 && status < 300) return 'ok';
  if (typeof reason === 'string' && reason) return clean(reason).slice(0, 40);
  return code ? clean(code).slice(0, 40) : status ? `http_${status}` : 'network';
}

/** Вид ссылки запуска из start_param: «match_123» → match, «tab_leagues» → tab; пусто — direct. */
export const launchSource = (start: string | undefined) => (start ? clean(start.split(/[_-]/)[0] ?? '').slice(0, 24) || 'other' : 'direct');

// ---- Рекламная метка (вторая часть start_param после «__», см. deeplink.splitStartParam) ----
export interface Campaign { source: string; medium: string; name: string; content: string }

/** Короткие имена сетей → как их зовёт Google Analytics; незнакомое имя идёт как есть (любая сетка). */
const SOURCES: Record<string, string> = { tg: 'telegram_ads', fb: 'facebook', ig: 'instagram', gg: 'google', yt: 'youtube', tt: 'tiktok', x: 'twitter', inf: 'influencer', ch: 'telegram_channel' };

/** Метка «<сеть>-<кампания>[-<объявление>]»: «fb-launch1-v2» → facebook / paid / launch1 / v2. Кнопка бота —
 *  «bot-<тип>» (Р-7): «bot-menu» → bot / notification / menu. Без метки — источник внутри Telegram по виду ссылки
 *  (ссылка на матч/команду — share, прочее — organic). */
export function campaignOf(label: string | null, dest: string | undefined): Campaign {
  if (label) {
    const bot = /^bot-(.+)$/i.exec(label);
    if (bot) return { source: 'bot', medium: 'notification', name: clean(bot[1]!).slice(0, 100) || 'unnamed', content: '' };
    const [src = '', name = '', ...rest] = label.split('-');
    const source = SOURCES[src.toLowerCase()] ?? (clean(src) || 'other');
    return { source, medium: 'paid', name: clean(name) || 'unnamed', content: clean(rest.join('_')) };
  }
  const kind = launchSource(dest);
  return { source: 'telegram', medium: kind === 'direct' || kind === 'home' ? 'organic' : 'share', name: kind, content: '' };
}

/** Адрес «страницы» с метками utm — по ним Google Analytics сам раскладывает сессии и первых пользователей по кампаниям. */
export function campaignQuery(c: Campaign): string {
  const q = new URLSearchParams({ utm_source: c.source, utm_medium: c.medium, utm_campaign: c.name });
  if (c.content) q.set('utm_content', c.content);
  return q.toString();
}

// ---- Ярлык на главном экране (HQ-772): лист на 3-й день визитов (auto) и строка меню (menu) ----
/** Событие предложения ярлыка. В словаре v1 бэкенда его пока нет — то же имя и параметры, что у Mini App крикета (HQ-725). */
export const HOME_SCREEN_PROMPT = 'home_screen_prompt';
export type HomeScreenPromptAction = 'shown' | 'accepted' | 'dismissed';
export type HomeScreenPromptSource = 'auto' | 'menu';
export const homeScreenPrompt = (action: HomeScreenPromptAction, source: HomeScreenPromptSource) => ({ action, source });
